import {
  DEVICE_TYPE_CODE_BY_NAME,
  DeviceTypeName,
  PLANT_MEASUREMENT_CODE_BY_NAME,
  PLANT_MEASUREMENT_NAME_BY_CODE,
  PlantMeasurementCodes,
} from '@/features/devices/schemas/devices.schemas.ts';
import {
  DEVICE_FIELDS_METADATA,
  getDeviceFieldByFieldId,
  PLANT_FIELDS_METADATA,
} from '@/features/devices/schemas/devices.metadata.ts';

export const OPERATORS = [
  { value: 'equal', label: 'Equal to' },
  { value: 'notEqual', label: 'Not equal to' },
  { value: 'lessThan', label: 'Less than' },
  { value: 'lessThanInclusive', label: 'Less than or equal to' },
  { value: 'greaterThan', label: 'Greater than' },
  { value: 'greaterThanInclusive', label: 'Greater than or equal to' },
];

export const getMessageExamples = (ruleType: string) => {
  const deviceExamples = [
    {
      title: 'Simple Event',
      template: '{{plantName}} requires attention',
    },
    {
      title: 'Battery Running Low',
      template:
        '{{plantName}} battery is at {{condition1Value}}% charge, below the safe level of {{condition1Threshold}}%.',
    },
    {
      title: 'String Connection Check',
      template:
        '{{plantName}} string configuration issue: {{condition1Threshold}} strings installed but only {{condition1Value}} strings connected.',
    },
    {
      title: 'Power Usage Too High',
      template:
        '{{plantName}} power use jumped to {{condition1Value}} kW at {{timestamp}}. Normal limit is {{condition1Threshold}} kW.',
    },
    {
      title: 'Device Temperature Alert',
      template:
        '{{deviceType}} (SN: {{deviceSN}}) temperature reached {{condition1Value}}°C, exceeding threshold of {{condition1Threshold}}°C.',
    },
    {
      title: 'Peak Shaving Opportunity',
      template:
        '{{plantName}} approaching peak demand period. Current load: {{condition1Value}} kW, peak threshold: {{condition1Threshold}} kW.',
    },
    {
      title: 'Multiple Conditions',
      template:
        '{{plantName}} alert: {{condition1Parameter}} is {{condition1Value}} and {{condition2Parameter}} is {{condition2Value}}.',
    },
  ];

  const plantExamples = [
    {
      title: 'Simple Event',
      template: '{{plantName}} requires attention',
    },
    {
      title: 'Plant Offline Warning',
      template: '{{plantName}} has gone {{condition1Value}}.',
    },
    {
      title: 'Connection Restored',
      template: '{{plantName}} connection has been restored to {{condition1Value}} status.',
    },
  ];

  return ruleType === 'device' ? deviceExamples : plantExamples;
};

export const getAvailableParameters = ({
  watchRuleType,
  watchDeviceType,
}: {
  watchRuleType: 'plant' | 'device';
  watchDeviceType: DeviceTypeName | undefined;
}) => {
  if (watchRuleType === 'device' && watchDeviceType) {
    const deviceTypeId = DEVICE_TYPE_CODE_BY_NAME[watchDeviceType as DeviceTypeName];
    const deviceMetadata = DEVICE_FIELDS_METADATA[deviceTypeId];

    if (deviceMetadata) {
      return Object.values(deviceMetadata.parametersByCode)
        .map((param) => ({
          id: param?.id,
          name: param?.name,
          unit: param?.unit,
        }))
        .sort((a, b) => a?.name?.localeCompare(b?.name));
    }
  }

  return Object.entries(PLANT_MEASUREMENT_CODE_BY_NAME)
    .map(([name, id]) => ({
      id: id,
      name: name,
      unit: undefined,
    }))
    .sort((a, b) => a?.name?.localeCompare(b?.name));
};

export function getParameterLabel({
  watchRuleType,
  watchDeviceType,
  fieldValue,
}: {
  watchRuleType: 'plant' | 'device';
  watchDeviceType: DeviceTypeName | undefined;
  fieldValue: number | string;
}) {
  if (watchRuleType === 'device' && watchDeviceType) {
    const field = getDeviceFieldByFieldId(watchDeviceType, Number(fieldValue));

    return field?.name;
  }

  return PLANT_MEASUREMENT_NAME_BY_CODE[fieldValue as PlantMeasurementCodes];
}

export function getDeviceParameterInfo(
  deviceType: DeviceTypeName | undefined,
  condition: {
    fact: string;
    operator: string;
    value: string;
  },
  index: number
) {
  if (!deviceType) return null;

  const paramName = getDeviceFieldByFieldId(deviceType, +condition.fact)?.name;
  if (!paramName) return null;

  return {
    fact: `{{condition${index + 1}Parameter}}`,
    value: `{{condition${index + 1}Value}}`,
    threshold: `{{condition${index + 1}Threshold}}`,
    paramName,
    thresholdValue: undefined,
  };
}

export function getPlantParameterInfo(
  condition: {
    fact: string;
    operator: string;
    value: string;
  },
  index: number
) {
  const mqttStatusCode = PLANT_MEASUREMENT_CODE_BY_NAME['MQTT Connection Status'];
  if (Number(condition.fact) !== mqttStatusCode) return null;

  const paramName = PLANT_MEASUREMENT_NAME_BY_CODE[mqttStatusCode];
  if (!paramName) return null;

  const options = Object.values(PLANT_FIELDS_METADATA[mqttStatusCode].parametersByCode);
  const thresholdValue = options?.find(({ id }) => id === Number(condition.value))?.name;

  return {
    fact: `{{condition${index + 1}Parameter}}`,
    value: `{{condition${index + 1}Value}}`,
    threshold: `{{condition${index + 1}Threshold}}`,
    paramName,
    thresholdValue: thresholdValue || undefined,
  };
}
